import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Box, Typography, Select, MenuItem, FormControl, InputLabel, Card, CardContent,
  Chip, CircularProgress, Button, Alert, Stack, Divider
} from '@mui/material';
import { ArrowBack, CallMerge, DoNotDisturb } from '@mui/icons-material';
import api from '../api';

interface Gericht {
  id: number;
  name: string;
  kategorie: string;
  subkategorie?: string;
  saison: string;
  datum: string;
}

interface Paar {
  aehnlichkeit: number;
  a: Gericht;
  b: Gericht;
}

const KATEGORIEN = ['Alle', 'Vorspeise', 'Suppe', 'Hauptgang', 'Dessert'];
const SCHWELLEN = [
  { wert: 0.8, label: 'Sehr ähnlich (ab 80 %)' },
  { wert: 0.7, label: 'Ähnlich (ab 70 %)' },
  { wert: 0.6, label: 'Entfernt ähnlich (ab 60 %)' },
];
const PRO_LADUNG = 30;

const wortNorm = (w: string) => w.toLowerCase().replace(/[^a-zäöüß]/g, '');

// Wörter, die im anderen Namen nicht vorkommen, hervorheben
function NameMitUnterschieden({ name, anderer }: { name: string; anderer: string }) {
  const andereWoerter = new Set(anderer.split(/[\s,-]+/).map(wortNorm));
  return (
    <Typography sx={{ fontWeight: 'bold' }}>
      {name.split(/(\s+)/).map((teil, i) => {
        const n = wortNorm(teil);
        const neu = n && !andereWoerter.has(n);
        return neu
          ? <Box key={i} component="span" sx={{ bgcolor: '#fff59d', borderRadius: 0.5, px: 0.25 }}>{teil}</Box>
          : <React.Fragment key={i}>{teil}</React.Fragment>;
      })}
    </Typography>
  );
}

function GerichtSeite({ g, anderer, onBehalten, beschaeftigt }: {
  g: Gericht; anderer: Gericht; onBehalten: () => void; beschaeftigt: boolean;
}) {
  return (
    <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
      <NameMitUnterschieden name={g.name} anderer={anderer.name} />
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
        {g.subkategorie && <Chip label={g.subkategorie} size="small" variant="outlined" />}
        {g.saison && <Chip label={g.saison} size="small" variant="outlined" />}
        {g.datum && (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {new Date(g.datum).toLocaleDateString('de-DE')}
          </Typography>
        )}
      </Box>
      <Box sx={{ mt: 'auto' }}>
        <Button size="small" variant="outlined" startIcon={<CallMerge />} onClick={onBehalten} disabled={beschaeftigt}>
          Diesen behalten
        </Button>
      </Box>
    </Box>
  );
}

export default function Duplikate({ onZurueck }: { onZurueck: () => void }) {
  const [kategorie, setKategorie] = useState('Alle');
  const [schwelle, setSchwelle] = useState(0.8);
  const [paare, setPaare] = useState<Paar[]>([]);
  const [gesamt, setGesamt] = useState(0);
  const [laden, setLaden] = useState(true);
  const [beschaeftigt, setBeschaeftigt] = useState(false);
  const [erledigt, setErledigt] = useState(0);
  const [fehler, setFehler] = useState('');
  const anfrage = useRef<AbortController | null>(null);

  const ladePaare = useCallback(() => {
    anfrage.current?.abort();
    const controller = new AbortController();
    anfrage.current = controller;
    const params: Record<string, string> = { schwelle: String(schwelle), limit: String(PRO_LADUNG) };
    if (kategorie !== 'Alle') params.kategorie = kategorie;
    setLaden(true);
    setFehler('');
    api.get('/gerichte/duplikate', { params, signal: controller.signal })
      .then(res => { if (!controller.signal.aborted) { setPaare(res.data.paare); setGesamt(res.data.gesamt); } })
      .catch(() => { if (!controller.signal.aborted) setFehler('Duplikate konnten nicht geladen werden.'); })
      .finally(() => { if (!controller.signal.aborted) setLaden(false); });
  }, [kategorie, schwelle]);

  useEffect(() => {
    ladePaare();
    return () => anfrage.current?.abort();
  }, [ladePaare]);

  // Wenn die geladene Liste abgearbeitet ist, nächste Ladung holen
  useEffect(() => {
    if (!laden && !fehler && paare.length === 0 && gesamt > 0) ladePaare();
  }, [laden, fehler, paare.length, gesamt, ladePaare]);

  const zusammenfuehren = async (behalten: Gericht, entfernen: Gericht) => {
    if (beschaeftigt) return;
    setBeschaeftigt(true);
    setFehler('');
    try {
      await api.post('/gerichte/duplikate/zusammenfuehren', {
        behalten_id: behalten.id, entfernen_id: entfernen.id,
      });
      // Auch noch nicht geladene Paare können vom Zusammenführen betroffen sein.
      ladePaare();
      setErledigt(n => n + 1);
    } catch {
      setFehler('Zusammenführen fehlgeschlagen.');
    } finally {
      setBeschaeftigt(false);
    }
  };

  const ignorieren = async (p: Paar) => {
    if (beschaeftigt) return;
    setBeschaeftigt(true);
    setFehler('');
    try {
      await api.post('/gerichte/duplikate/ignorieren', { id_a: p.a.id, id_b: p.b.id });
      setPaare(prev => prev.filter(x => x !== p));
      setGesamt(g => g - 1);
      setErledigt(n => n + 1);
    } catch {
      setFehler('Speichern fehlgeschlagen.');
    } finally {
      setBeschaeftigt(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Button disabled={beschaeftigt} startIcon={<ArrowBack />} onClick={onZurueck}>Zur Übersicht</Button>
      </Box>
      <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>Duplikate prüfen</Typography>
      <Typography sx={{ color: 'text.secondary', mb: 3 }}>
        Beim Behalten werden Saisonen zusammengeführt und das früheste Datum übernommen, das andere Gericht wird gelöscht.
        Gelb markiert sind Wörter, die nur in einem der beiden Namen vorkommen.
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Kategorie</InputLabel>
          <Select disabled={beschaeftigt} value={kategorie} label="Kategorie" onChange={e => setKategorie(e.target.value)}>
            {KATEGORIEN.map(k => <MenuItem key={k} value={k}>{k}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Ähnlichkeit</InputLabel>
          <Select disabled={beschaeftigt} value={schwelle} label="Ähnlichkeit" onChange={e => setSchwelle(Number(e.target.value))}>
            {SCHWELLEN.map(s => <MenuItem key={s.wert} value={s.wert}>{s.label}</MenuItem>)}
          </Select>
        </FormControl>
        {erledigt > 0 && <Chip label={`${erledigt} erledigt`} color="success" size="small" />}
      </Box>

      {fehler && <Alert severity="error" sx={{ mb: 2 }} action={<Button disabled={beschaeftigt} onClick={ladePaare}>Erneut laden</Button>}>{fehler}</Alert>}

      {laden ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>
      ) : fehler ? null : gesamt === 0 ? (
        <Alert severity="success">Keine möglichen Duplikate mit dieser Ähnlichkeit gefunden.</Alert>
      ) : (
        <>
          <Typography sx={{ color: 'text.secondary', mb: 2 }}>{gesamt} mögliche Duplikate</Typography>
          <Stack spacing={2}>
            {paare.map(p => (
              <Card key={`${p.a.id}-${p.b.id}`} elevation={2}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {p.a.kategorie} · {Math.round(p.aehnlichkeit * 100)} % ähnlich
                    </Typography>
                    <Button size="small" color="inherit" startIcon={<DoNotDisturb />}
                      onClick={() => ignorieren(p)} disabled={beschaeftigt}>
                      Kein Duplikat
                    </Button>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                    <GerichtSeite g={p.a} anderer={p.b} beschaeftigt={beschaeftigt} onBehalten={() => zusammenfuehren(p.a, p.b)} />
                    <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
                    <Divider sx={{ display: { xs: 'block', sm: 'none' } }} />
                    <GerichtSeite g={p.b} anderer={p.a} beschaeftigt={beschaeftigt} onBehalten={() => zusammenfuehren(p.b, p.a)} />
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Stack>
        </>
      )}
    </Box>
  );
}
